import { SiteUrls } from '@tableau/shared-urls';
import * as React from 'react';
import { defaultMemoize } from 'reselect';
import * as _ from 'underscore';

import { populateContentBreadcrumbs } from '~src/app/content/breadcrumbs/BreadcrumbHelpers';
import { SpacesSiteUrls } from '~src/app/spaces/SpacesSiteUrls';
import { maybeSendTelemetry_favoriteItemOpened } from '~src/app/spaces/SpaceTelemetry';
import { explorerSubspace } from '~src/app/spaces/SubspaceHelpers';
import { Subspace, TabbedSpace } from '~src/app/spaces/TabbedSpace';
import { ISpaceInfoTypeItemCount } from '~src/app/state/ContentCountsState';
import { PublishedConnectionTableSubspaceName } from '~src/app/state/SpaceInfo';
import { Messages } from '~src/codegen/Localize';
import { PublishedConnectionTableDefinition } from '~src/contentDefinitions/PublishedConnectionTableDefinition';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { IPublishedConnectionDetailInfo } from '~src/ts/models/IPublishedConnectionInfo';
import { IPublishedConnectionTableDetailInfo, IPublishedConnectionTableInfo } from '~src/ts/models/IPublishedConnectionTableInfo';
import { IExplorableItemTypeInfo, INamedItemInfo } from '~src/ts/models/resourceInfoParts';
import { IPublishedConnectionTableActions } from '~src/ts/ServerApi/types/IPublishedConnectionTableActions';

import { PublishedConnectionTableSubplaceProps } from '../types/PublishedConnectionTableSubplaceProps';
import { PublishedConnectionTableSpaceHeader } from './PublishedConnectionTableSpaceHeader';

export type PublishedConnectionTableSpaceStateProps = {
  readonly contentCountsPublishedConnectionTable: ISpaceInfoTypeItemCount<'publishedConnectionTable'>;
  readonly contentCountsUpToDate: boolean;
  readonly urls: SpacesSiteUrls;
  readonly catalogingOnSiteEnabled: boolean;
  readonly areUsersVisible: boolean;
};

export type PublishedConnectionTableSpaceDispatchProps = {
  readonly addToFavorites: () => void;
  readonly enterSpace: (requestContent: boolean) => void;
  readonly fetchUpdatedContentCounts: () => void;
  readonly markContentCountsUpToDate: () => void;
  readonly removeFromFavorites: () => void;
};

export interface PublishedConnectionTableSpaceOwnProps {
  readonly publishedConnectionDetailInfo: IPublishedConnectionDetailInfo;
  readonly publishedConnectionTableAllowedActions: IPublishedConnectionTableActions;
  readonly publishedConnectionTableDetailInfo: IPublishedConnectionTableDetailInfo;
  readonly publishedConnectionTableInfo: IPublishedConnectionTableInfo;
  readonly projectHierarchy: INamedItemInfo[];

  readonly refreshData: () => void;

  // subplace info
  readonly subplaceName: string;
  readonly siteUrls: SiteUrls;
}

export type PublishedConnectionTableSpaceProps =
  PublishedConnectionTableSpaceStateProps
  & PublishedConnectionTableSpaceDispatchProps
  & PublishedConnectionTableSpaceOwnProps;

function overviewSubspace(subplaceProps: PublishedConnectionTableSubplaceProps): Subspace {
  return explorerSubspace({
    contentCount: getContentCount(subplaceProps.contentCountsPublishedConnectionTable, 'columns'),
    contentCountUpToDate: subplaceProps.contentCountsUpToDate,
    emptyStateProps: {
      primaryMessage: Messages.column_table_columns()
    },
    hideSelection: false,
    name: 'overview',
    shouldRenderContentCount: true,
    supportedDisplayModes: PublishedConnectionTableDefinition.supportedDisplayModes,
    supportFilterPanel: false,
    tabLabel: Messages.column_table_columns(),
    toolbarVisibility: 'enabled',
  });
}

function lineageSubspace(id: string, siteUrls: SiteUrls, areUsersVisible: boolean): Subspace {
  return {
    name: 'lineage',
    tabLabel: Messages.dataCatalogLineage(),
    shouldRenderTabContentCount: false,
    renderContent: () =>
      <div/>,
  };
}

function getPublishedConnectionTableDetailWithUsage(
  publishedConnectionTableDetailInfo: IPublishedConnectionTableDetailInfo,
  publishedConnectionTableInfo: IPublishedConnectionTableInfo,
) {
  return {
    usageInfo: publishedConnectionTableInfo.usageInfo,
    usageInfoMaxValues: publishedConnectionTableInfo.usageInfoMaxValues,
    ...publishedConnectionTableDetailInfo,
  };
}

function getPublishedConnectionTableSubspaces(subplaceProps: PublishedConnectionTableSubplaceProps) {
  return _.compact([
    overviewSubspace(subplaceProps),
    //FeatureFlags.publishedConnectionExtractSchedulingEnabled && scheduledTasksSubspace(subplaceProps),
    FeatureFlags.publishedConnectionsLineageEnabled &&
      subplaceProps.catalogingOnSiteEnabled &&
      lineageSubspace(subplaceProps.publishedConnectionTableDetailInfo.id, subplaceProps.siteUrls, subplaceProps.areUsersVisible),
  ]);
}

function getContentCount(contentCountsPublishedConnectionTable: ISpaceInfoTypeItemCount<'publishedConnectionTable'>, contentName: string): number {
  return contentCountsPublishedConnectionTable && contentCountsPublishedConnectionTable[contentName as PublishedConnectionTableSubspaceName];
}

export class PublishedConnectionTableSpace extends React.PureComponent<PublishedConnectionTableSpaceProps> {
  private memoizedPublishedConnectionTableDetailWithUsage = defaultMemoize(getPublishedConnectionTableDetailWithUsage);
  private memoizedGetPublishedConnectionTableSubspaces = defaultMemoize(getPublishedConnectionTableSubspaces);

  public urlFromSubspaceName = (subspaceName?: PublishedConnectionTableSubspaceName) => {
    switch (subspaceName) {
      case 'overview':
        return this.props.urls.publishedConnectionTableOverviewUrl(this.props.publishedConnectionTableDetailInfo.id);
      case 'scheduledTasks':
        return this.props.urls.publishedConnectionTableScheduledTasksUrl(this.props.publishedConnectionTableDetailInfo.id);
      case 'lineage':
        return this.props.urls.publishedConnectionTableLineageUrl(this.props.publishedConnectionDetailInfo.id);
    }
  };

  public render() {
    const subplaceProps: PublishedConnectionTableSubplaceProps = {
      contentCountsPublishedConnectionTable: this.props.contentCountsPublishedConnectionTable,
      contentCountsUpToDate: this.props.contentCountsUpToDate,
      fetchUpdatedContentCounts: this.props.fetchUpdatedContentCounts,
      publishedConnectionTableAllowedActions: this.props.publishedConnectionTableAllowedActions,
      publishedConnectionTableDetailInfo: this.props.publishedConnectionTableDetailInfo,
      refreshData: this.props.refreshData,
      siteUrls: this.props.urls,
      areUsersVisible: this.props.areUsersVisible,
      catalogingOnSiteEnabled: this.props.catalogingOnSiteEnabled
    };

    const subspaces = this.memoizedGetPublishedConnectionTableSubspaces(subplaceProps);

    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={this.props.subplaceName as PublishedConnectionTableSubspaceName}
        urlForSubspace={this.urlFromSubspaceName}
        mastheadElement={this.mastheadElement()}
        testIdPrefix={'publishedConnectionTable'}
        spaceItemName={this.props.publishedConnectionTableDetailInfo.name}
      />
    );
  }

  public componentDidMount() {
    maybeSendTelemetry_favoriteItemOpened(this.props.publishedConnectionTableDetailInfo);
    this.changeSubspace(true);
    this.updateBreadcrumbs();
  }

  public componentDidUpdate(prevProps: PublishedConnectionTableSpaceProps) {
    if (this.props.publishedConnectionTableDetailInfo.id !== prevProps.publishedConnectionTableDetailInfo.id) {
      maybeSendTelemetry_favoriteItemOpened(this.props.publishedConnectionTableDetailInfo);
      this.changeSubspace(true);
      this.updateBreadcrumbs();
    } else if (this.props.subplaceName !== prevProps.subplaceName) {
      this.changeSubspace(false);
    }
  }

  private shouldRequestContent() {
    // tasks are fetched from within the component, so no need to request via mixed content action
    switch (this.props.subplaceName) {
      case 'overview':
        return true;
      case 'scheduledTasks':
        return false;
    }
  }

  private changeSubspace(didPublishedConnectionTableChange: boolean) {
    this.props.enterSpace(this.shouldRequestContent());
    if (didPublishedConnectionTableChange) {
      this.props.fetchUpdatedContentCounts();
    } else {
      // enterSpace action causes counts out of date.
      // We mark it upto date because there is no need to fetch count again for same published connection.
      this.props.markContentCountsUpToDate();
    }
  }

  private updateBreadcrumbs() {
    const publishedConnection: IExplorableItemTypeInfo<'publishedConnection'> = {
      id: this.props.publishedConnectionDetailInfo.id,
      name: this.props.publishedConnectionDetailInfo.name,
      contentType: 'publishedConnection'
    };
    populateContentBreadcrumbs([publishedConnection, this.props.publishedConnectionTableDetailInfo], this.props.urls);
  }

  private mastheadElement(): React.ReactNode {
    const publishedConnectionTableDetailWithUsage = this.memoizedPublishedConnectionTableDetailWithUsage(
      this.props.publishedConnectionTableDetailInfo,
      this.props.publishedConnectionTableInfo,
    );

    return (
      <PublishedConnectionTableSpaceHeader
        itemInfo={publishedConnectionTableDetailWithUsage}
        availableActions={this.props.publishedConnectionTableAllowedActions}
        openDetail={() => null}
        addToFavorites={this.props.addToFavorites}
        removeFromFavorites={this.props.removeFromFavorites}
        urls={this.props.urls}
      />
    );
  }
}
